import { Show, type JSX } from 'solid-js';
import { Spinner } from './busy';
import './Switch.css';

interface SwitchProps {
  checked: boolean;
  disabled?: boolean;
  /**
   * The flip has been sent and the controller has not answered yet.
   *
   * The switch itself has ALREADY moved — a toggle that waits for a round trip before it shows the
   * operator's own press reads as a dead control — so this says only that the room is catching up.
   */
  busy?: boolean;
  /** What the switch is called, for anyone not looking at the screen. */
  label: string;
  testId?: string;
  onChange: (checked: boolean) => void;
}

/**
 * The one toggle in the console.
 *
 * There were two before — a hand-built track-and-knob beside "Sync to music", and a bare native
 * checkbox for every boolean the generic effect panel generates — so the same idea looked like two
 * different controls on the same screen, and only one of them had a disabled state worth the name.
 *
 * It is deliberately built to survive the global form reset rather than to depend on it. `styles.css`
 * gives every `input` a `min-height` and a border meant for text fields, and every `label > span` a
 * bottom margin meant for field captions; both leaked in and deformed the old switch — the track was
 * squeezed 7px shorter than the box it was positioned inside, which pushed the knob out through its
 * edge. So every property those rules touch is set explicitly here. A control must not be correct only
 * because of what a stylesheet elsewhere happens not to say.
 */
export function Switch(props: SwitchProps): JSX.Element {
  return (
    <span class="switch" classList={{ 'switch-disabled': props.disabled }}>
      <Show when={props.busy}><Spinner class="switch-spinner" /></Show>
      <input
        type="checkbox"
        role="switch"
        checked={props.checked}
        disabled={props.disabled}
        aria-label={props.label}
        data-testid={props.testId}
        onChange={(event) => props.onChange(event.currentTarget.checked)}
      />
      <span class="switch-track" aria-hidden="true"><span class="switch-knob" /></span>
    </span>
  );
}
